import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import { parseError } from '@/hooks/useTeams';

export interface PromptOverride {
  slug: string;
  title: string;
  description: string;
  // The prompt in use: the stored one, or the default when none is stored
  prompt: string;
  default_prompt: string;
  is_overridden: boolean;
}

const PROMPTS_KEY = ['prompt-overrides'];

function usePromptOverrideFetch() {
  const { getAccessTokenSilently } = useAuth0();
  return async <T>(
    path: string,
    method: 'GET' | 'PUT' | 'DELETE' = 'GET',
    body?: { prompt: string }
  ): Promise<T> => {
    const res = await apiFetch(`${API_URL}/admin/prompt-overrides${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${await getAccessTokenSilently()}`,
        'Content-Type': 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json() as Promise<T>;
  };
}

export function usePromptOverrides() {
  const fetchPromptOverrides = usePromptOverrideFetch();
  return useQuery({
    queryKey: PROMPTS_KEY,
    queryFn: () => fetchPromptOverrides<PromptOverride[]>(''),
  });
}

export function usePromptOverride(slug: string) {
  const fetchPromptOverride = usePromptOverrideFetch();
  return useQuery({
    queryKey: [...PROMPTS_KEY, slug],
    queryFn: () => fetchPromptOverride<PromptOverride>(`/${slug}`),
  });
}

function usePromptOverrideMutation<TVars>(
  slug: string,
  toRequest: (vars: TVars) => {
    method: 'PUT' | 'DELETE';
    body?: { prompt: string };
  }
) {
  const fetchPromptOverride = usePromptOverrideFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: TVars) => {
      const { method, body } = toRequest(vars);
      return fetchPromptOverride<PromptOverride>(`/${slug}`, method, body);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData([...PROMPTS_KEY, slug], saved);
      void queryClient.invalidateQueries({
        queryKey: PROMPTS_KEY,
        exact: true,
      });
    },
  });
}

export function useSavePromptOverride(slug: string) {
  return usePromptOverrideMutation(slug, (prompt: string) => ({
    method: 'PUT',
    body: { prompt },
  }));
}

export function useResetPromptOverride(slug: string) {
  return usePromptOverrideMutation<void>(slug, () => ({ method: 'DELETE' }));
}
